import {
    Article,
    ArticleOutlined,
    Clear,
    GridView,
    ImageOutlined,
    List as ListIcon,
    Videocam,
    VideocamOutlined,
    VisibilityOutlined,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    MenuItem,
    Select,
    styled,
    Theme,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { Guide } from '../../../../types/experience';

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
    textTransform: 'none',
    gap: theme.spacing(0.5),
}));

const formatGuideType = (type: string) => {
    if (type.startsWith('video')) return 'video';
    if (type.startsWith('image')) return 'image';
    return 'file';
};

const GuideListActions = ({
    filter,
    view,
    handleFilter,
    handleView,
}: {
    filter: string;
    view: 'grid' | 'list';
    handleFilter: (filter: string) => void;
    handleView: (view: 'grid' | 'list') => void;
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 2,
                gap: 2,
            }}
        >
            {isMobile ? (
                <Select
                    size='small'
                    value={filter}
                    onChange={(e) => handleFilter(e.target.value)}
                    fullWidth
                >
                    <MenuItem value='all'>All</MenuItem>
                    <MenuItem value='video'>
                        <VideocamOutlined fontSize='small' /> Videos
                    </MenuItem>
                    <MenuItem value='image'>
                        <ImageOutlined fontSize='small' /> Images
                    </MenuItem>
                    <MenuItem value='file'>
                        <ArticleOutlined fontSize='small' /> Documents
                    </MenuItem>
                </Select>
            ) : (
                <ToggleButtonGroup
                    size='small'
                    value={filter}
                    sx={(theme) => ({
                        gap: 2,
                        '& .MuiToggleButtonGroup-grouped': {
                            border: '1px solid rgba(0,0,0,0.12) !important',
                            borderRadius: `${theme.spacing(0.5)} !important`,
                        },
                    })}
                    onChange={(_, v) => handleFilter(v)}
                    exclusive
                >
                    <StyledToggleButton value='all'>All</StyledToggleButton>
                    <StyledToggleButton value='video'>
                        <VideocamOutlined fontSize='small' /> Videos
                    </StyledToggleButton>
                    <StyledToggleButton value='image'>
                        <ImageOutlined fontSize='small' /> Images
                    </StyledToggleButton>
                    <StyledToggleButton value='file'>
                        <ArticleOutlined fontSize='small' /> Documents
                    </StyledToggleButton>
                </ToggleButtonGroup>
            )}
            <ToggleButtonGroup
                size='small'
                value={view}
                onChange={(_, v) => handleView(v)}
                exclusive
            >
                <ToggleButton value='grid'>
                    <GridView />
                </ToggleButton>
                <ToggleButton value='list'>
                    <ListIcon />
                </ToggleButton>
            </ToggleButtonGroup>
        </Box>
    );
};

const GuideListGridView = ({
    guides,
    handleRemove,
}: {
    guides: Guide[];
    handleRemove?: (index: number) => void;
}) => {
    const handleView = (src: string) =>
        window.open(src, '_blank', 'noopener noreferrer');

    return (
        <Grid container spacing={2}>
            {guides.map((guide, index) => (
                <Grid key={guide.id} item xs={12} sm={6} md={4} lg={3}>
                    <Card
                        variant='outlined'
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                        }}
                    >
                        <Chip
                            size='small'
                            color='primary'
                            label={formatGuideType(
                                guide.mimeType,
                            ).toUpperCase()}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                zIndex: 1,
                            }}
                        />
                        {formatGuideType(guide.mimeType) === 'video' && (
                            <img
                                src={guide.thumbnail}
                                width='100%'
                                height={200}
                                style={{
                                    objectFit: 'cover',
                                    cursor: 'pointer',
                                }}
                                onClick={() => handleView(guide.src)}
                            />
                        )}
                        {formatGuideType(guide.mimeType) === 'image' && (
                            <img
                                src={guide.src}
                                width='100%'
                                height={200}
                                style={{
                                    objectFit: 'cover',
                                    cursor: 'pointer',
                                }}
                                onClick={() => handleView(guide.src)}
                            />
                        )}
                        {formatGuideType(guide.mimeType) === 'file' && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    bgcolor: 'faded.main',
                                    width: '100%',
                                    height: '200px',
                                }}
                            >
                                <Article sx={{ m: 'auto' }} />
                            </Box>
                        )}
                        <CardContent>
                            <Typography>{guide.name}</Typography>
                        </CardContent>
                        <CardActions sx={{ mt: 'auto' }}>
                            <Button
                                size='small'
                                variant='outlined'
                                onClick={() =>
                                    window.open(
                                        guide.src,
                                        '_blank',
                                        'noopener noreferrer',
                                    )
                                }
                                startIcon={<VisibilityOutlined />}
                            >
                                View
                            </Button>
                            {handleRemove && (
                                <Button
                                    size='small'
                                    variant='outlined'
                                    onClick={() => handleRemove(index)}
                                    startIcon={<Clear />}
                                >
                                    Remove
                                </Button>
                            )}
                        </CardActions>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

const GuideListView = ({
    guides,
    handleRemove,
}: {
    guides: Guide[];
    handleRemove?: (index: number) => void;
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleView = (src: string) =>
        window.open(src, '_blank', 'noopener noreferrer');

    return (
        <List>
            {guides.map((guide, index) => {
                const type = formatGuideType(guide.mimeType);

                return (
                    <ListItem key={guide.id} disableGutters divider>
                        <Box
                            sx={{
                                display: 'flex',
                                bgcolor: 'faded.main',
                                width: 50,
                                aspectRatio: 1,
                                borderRadius: 2,
                                mr: 2,
                            }}
                        >
                            {type === 'video' ? (
                                <Videocam sx={{ m: 'auto' }} />
                            ) : type === 'image' ? (
                                <ImageOutlined sx={{ m: 'auto' }} />
                            ) : (
                                <Article sx={{ m: 'auto' }} />
                            )}
                        </Box>
                        <ListItemText primary={guide.name} />
                        <ListItemSecondaryAction>
                            {isMobile ? (
                                <>
                                    <IconButton
                                        size='small'
                                        onClick={() => handleView(guide.src)}
                                    >
                                        <VisibilityOutlined fontSize='small' />
                                    </IconButton>
                                    {handleRemove && (
                                        <IconButton
                                            size='small'
                                            onClick={() => handleRemove(index)}
                                        >
                                            <Clear fontSize='small' />
                                        </IconButton>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Button
                                        size='small'
                                        variant='outlined'
                                        onClick={() => handleView(guide.src)}
                                        startIcon={<VisibilityOutlined />}
                                    >
                                        View
                                    </Button>
                                    {handleRemove && (
                                        <Button
                                            size='small'
                                            variant='outlined'
                                            onClick={() => handleRemove(index)}
                                            startIcon={<Clear />}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </>
                            )}
                        </ListItemSecondaryAction>
                    </ListItem>
                );
            })}
        </List>
    );
};

const GuideList = ({
    guides,
    handleRemove,
}: {
    guides: Guide[];
    handleRemove?: (index: number) => void;
}) => {
    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('sm'),
    );

    const [filter, setFilter] = useState('all');
    const [view, setView] = useState<'grid' | 'list'>(
        isMobile ? 'list' : 'grid',
    );

    const filteredGuides = guides.filter(
        (guide) =>
            filter === 'all' || formatGuideType(guide.mimeType) === filter,
    );

    return (
        <>
            <GuideListActions
                filter={filter}
                view={view}
                handleFilter={(filter) => setFilter(filter)}
                handleView={(view) => setView(view)}
            />
            {view === 'grid' && (
                <GuideListGridView
                    guides={filteredGuides}
                    handleRemove={handleRemove}
                />
            )}
            {view === 'list' && (
                <GuideListView
                    guides={filteredGuides}
                    handleRemove={handleRemove}
                />
            )}
        </>
    );
};

export default GuideList;
